import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, BookOpen, Heart, Mail, Clapperboard, Megaphone, Send, Ticket, Trash2, Ban, Eye, AlertTriangle, MessageSquare, ShieldCheck, RotateCcw, BarChart3, Bookmark, Building2, Smile, FileText } from "lucide-react";
import { useLang } from "../i18n";
import { useAuth } from "../auth";
import api, { formatApiError } from "../api";
import Reveal from "../components/Reveal";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtAmount = (cents, cur = "eur") => `${(cents / 100).toFixed(2)} ${cur === "eur" ? "€" : cur}`;

const Pager = ({ data, page, setPage, testid }) => {
  if (!data || data.pages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4" data-testid={testid}>
      <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`}
        className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-mono2 uppercase tracking-wider text-mist hover:border-ember hover:text-ember disabled:opacity-30 transition-colors">
        ← Prec
      </button>
      <span className="font-mono2 text-[10px] tracking-widest text-mute uppercase">{data.page} / {data.pages} · {data.total}</span>
      <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`}
        className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-mono2 uppercase tracking-wider text-mist hover:border-ember hover:text-ember disabled:opacity-30 transition-colors">
        Succ →
      </button>
    </div>
  );
};

const Bacheca = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sendState, setSendState] = useState(null);
  const [openReading, setOpenReading] = useState(null);
  const [readingMap, setReadingMap] = useState({});
  const [readersData, setReadersData] = useState(null);
  const [paymentsData, setPaymentsData] = useState(null);
  const [errorsData, setErrorsData] = useState(null);
  const [rPage, setRPage] = useState(1);
  const [rSearch, setRSearch] = useState("");
  const [pPage, setPPage] = useState(1);
  const [ePage, setEPage] = useState(1);
  const [gBusy, setGBusy] = useState(false);
  const [restoreMsg, setRestoreMsg] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyMsg, setNotifyMsg] = useState(null);
  const [coForm, setCoForm] = useState(null);
  const [coMsg, setCoMsg] = useState(null);
  const [promoKind, setPromoKind] = useState("free");
  const [invoiceRequests, setInvoiceRequests] = useState(null);
  const [fic, setFic] = useState(null);
  const [ficForm, setFicForm] = useState({});
  const [ficMsg, setFicMsg] = useState(null);
  const [invRefresh, setInvRefresh] = useState(0);

  useEffect(() => {
    if (data?.company && !coForm) setCoForm(data.company);
  }, [data?.company]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCompany = async () => {
    setCoMsg(null);
    try {
      await api.put("/admin/settings/company", coForm);
      setCoMsg({ ok: true, text: t("b_notify_saved") });
    } catch (err) {
      setCoMsg({ ok: false, text: formatApiError(err) });
    }
  };

  useEffect(() => {
    if (data?.notifyEmail && !notifyEmail) setNotifyEmail(data.notifyEmail);
  }, [data?.notifyEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveNotifyEmail = async () => {
    setNotifyMsg(null);
    try {
      await api.put("/admin/settings/notify-email", { notify_email: notifyEmail });
      setNotifyMsg({ ok: true, text: t("b_notify_saved") });
    } catch (err) {
      setNotifyMsg({ ok: false, text: formatApiError(err) });
    }
  };

  const toggleContactRead = async (contactId) => {
    try {
      const { data: res } = await api.patch(`/admin/contacts/${contactId}/read`);
      setData((d) => ({ ...d, contacts: d.contacts.map((c) => (c.contact_id === contactId ? { ...c, read: res.read } : c)) }));
    } catch { /* riprova dopo */ }
  };

  const rollbackAction = async (id) => {    try {
      await api.post(`/admin/guardian/rollback/${id}`);
      setData((d) => ({ ...d, gactions: d.gactions.map((x) => (x.action_id === id ? { ...x, status: "rolled_back" } : x)) }));
    } catch { /* azione non annullabile */ }
  };

  const runGuardian = async () => {
    setGBusy(true);
    try {
      const rep = await api.post("/admin/guardian/run");
      const [g, gr, ga] = await Promise.all([api.get("/admin/guardian/overview"), api.get("/admin/guardian/reports"), api.get("/admin/guardian/actions")]);
      setData((d) => ({ ...d, guardian: g.data, greports: gr.data, gactions: ga.data, lastRun: rep.data }));
    } catch { /* riprova dopo */ }
    setGBusy(false);
  };

  const requestVersionRollback = async (hash) => {
    try {
      await api.post("/admin/guardian/versions/rollback-request", { hash });
      setRestoreMsg(hash);
      setTimeout(() => setRestoreMsg(null), 3000);
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    Promise.all([api.get("/admin/overview"), api.get("/admin/contacts"), api.get("/admin/newsletter"), api.get("/admin/promos"), api.get("/admin/comments"),
      api.get("/admin/guardian/overview"), api.get("/admin/guardian/reports"), api.get("/admin/guardian/actions"), api.get("/admin/guardian/versions"),
      api.get("/admin/stats/readers"), api.get("/admin/settings/notify-email"), api.get("/admin/settings/company")])
      .then(([o, c, n, pr, cm, g, gr, ga, gv, rs, ne, co]) => setData({ overview: o.data, contacts: c.data, newsletter: n.data, promos: pr.data, comments: cm.data, guardian: g.data, greports: gr.data, gactions: ga.data, gversions: gv.data, rstats: rs.data, notifyEmail: ne.data.notify_email, company: co.data }))
      .catch(() => setData({ forbidden: true }));
  }, [user]);

  useEffect(() => {
    if (user?.role === "admin") api.get(`/admin/readers?page=${rPage}&search=${encodeURIComponent(rSearch)}`).then(({ data }) => setReadersData(data)).catch(() => {});
  }, [user, rPage, rSearch]);
  useEffect(() => {
    if (user?.role === "admin") api.get(`/admin/payments?page=${pPage}`).then(({ data }) => setPaymentsData(data)).catch(() => {});
  }, [user, pPage]);
  useEffect(() => {
    if (user?.role === "admin") api.get(`/admin/errors?page=${ePage}`).then(({ data }) => setErrorsData(data)).catch(() => {});
  }, [user, ePage]);
  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/invoice-requests").then(({ data }) => setInvoiceRequests(data)).catch(() => {});
      api.get("/admin/fatture/settings").then(({ data }) => setFic(data)).catch(() => {});
    }
  }, [user, invRefresh]);

  const toggleSuspend = async (u) => {
    await api.post(`/admin/users/${u.user_id}/suspend`, { suspended: !u.suspended });
    setReadersData((d) => ({ ...d, items: d.items.map((x) => (x.user_id === u.user_id ? { ...x, suspended: !u.suspended } : x)) }));
  };

  const toggleReading = async (u) => {
    if (openReading === u.user_id) { setOpenReading(null); return; }
    setOpenReading(u.user_id);
    if (!readingMap[u.user_id]) {
      try {
        const { data: rows } = await api.get(`/admin/users/${u.user_id}/reading`);
        setReadingMap((m) => ({ ...m, [u.user_id]: rows }));
      } catch { setReadingMap((m) => ({ ...m, [u.user_id]: [] })); }
    }
  };

  if (user === false) return <Navigate to="/accedi?return=/bacheca" replace />;
  if (user && user.role !== "admin") {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center" data-testid="bacheca-forbidden">
        <p className="text-mist">{t("dash_forbidden")}</p>
      </main>
    );
  }

  const o = data?.overview;
  const cards = [
    { icon: Users, label: t("dash_readers"), value: o?.readers, color: "#FF5500" },
    { icon: BookOpen, label: t("dash_unlocked"), value: o?.unlocks_count, color: "#8B2FC9" },
    { icon: Heart, label: t("dash_donations"), value: o ? `${o.donations_total.toFixed(0)} €` : "…", color: "#FFB800" },
    { icon: Mail, label: t("dash_contacts"), value: o?.contacts, color: "#FF5500" },
    { icon: Megaphone, label: t("b_subs"), value: o?.newsletter, color: "#FFB800" },
    { icon: Clapperboard, label: t("dash_videos"), value: o ? `${o.videos_ready}/28` : "…", color: "#8B2FC9" },
  ];

  const statusColor = (s) => (s === "paid" ? "text-gold border-gold/50 bg-gold/10" : s === "failed" || s === "refunded" ? "text-red-400 border-red-400/40 bg-red-400/10" : "text-mist border-white/15");

  const refundPayment = async (sessionId) => {
    if (!window.confirm(t("dash_refund_confirm"))) return;
    try {
      await api.post(`/admin/payments/${sessionId}/refund`);
      setPaymentsData((d) => ({ ...d, items: d.items.map((x) => (x.session_id === sessionId ? { ...x, payment_status: "refunded" } : x)) }));
    } catch (err) {
      window.alert(formatApiError(err));
    }
  };

  const requestInvoice = async (sessionId) => {
    try {
      await api.post(`/admin/payments/${sessionId}/invoice-request`);
      setInvRefresh((n) => n + 1);
    } catch (err) {
      window.alert(formatApiError(err));
    }
  };

  const saveFic = async (e) => {
    e.preventDefault();
    setFicMsg(null);
    try {
      await api.put("/admin/fatture/settings", ficForm);
      setFicMsg({ ok: true, text: t("b_inv_saved") });
      setFicForm({});
      setInvRefresh((n) => n + 1);
    } catch (err) {
      setFicMsg({ ok: false, text: formatApiError(err) });
    }
  };

  const testFic = async () => {
    setFicMsg(null);
    try {
      await api.post("/admin/fatture/test");
      setFicMsg({ ok: true, text: t("b_inv_test_ok") });
    } catch (err) {
      setFicMsg({ ok: false, text: formatApiError(err) });
    }
  };

  const issueDoc = async (requestId) => {
    try {
      await api.post(`/admin/invoice-requests/${requestId}/issue`);
      setInvRefresh((n) => n + 1);
    } catch (err) {
      window.alert(formatApiError(err));
      setInvRefresh((n) => n + 1);
    }
  };

  return (
    <main className="pt-16" data-testid="bacheca-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-24">
        <Reveal>
          <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-ember mb-5">Admin</p>
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mb-3" data-testid="bacheca-title">{t("dash_title")}</h1>
          <p className="text-mist mb-8 text-sm sm:text-base">{t("dash_sub")}</p>
        </Reveal>

        <div className="sticky top-16 z-30 -mx-5 sm:-mx-8 px-5 sm:px-8 py-3 bg-ink/85 backdrop-blur-xl border-y border-white/10 mb-10 overflow-x-auto" data-testid="bacheca-menu">
          <div className="flex gap-2 min-w-max">
            {[
              ["stats", t("rs_title")], ["guardian", t("g_title")], ["errors", t("b_errors")],
              ["payments", t("dash_payments")], ["invoices", t("b_invoices")], ["newsletter", t("b_newsletter")], ["promos", t("b_promos")],
              ["readers", t("dash_readers_list")], ["contacts", t("dash_contacts_list")],
              ["comments", t("b_comments")], ["config", t("b_co_title") + " · " + t("b_notify_title")],
            ].map(([id, label]) => (
              <button key={id} data-testid={`bacheca-menu-${id}`}
                onClick={() => document.getElementById(`bk-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="rounded-full border border-white/15 px-4 py-1.5 text-[11px] font-mono2 uppercase tracking-wider text-mist hover:text-ember hover:border-ember transition-colors whitespace-nowrap">
                {label}
              </button>
            ))}
          </div>
        </div>

        <div id="bk-overview" className="scroll-mt-32 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-14" data-testid="bacheca-stats">
          {cards.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.05}>
              <div className="rounded-xl border border-white/10 bg-card p-5">
                <c.icon className="w-5 h-5 mb-3" style={{ color: c.color }} />
                <p className="font-display font-extrabold text-2xl">{c.value ?? "…"}</p>
                <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mute mt-1">{c.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal id="bk-payments" className="scroll-mt-32">
          <h2 className="font-display font-bold text-xl mb-5">{t("dash_payments")}</h2>
          <div className="rounded-xl border border-white/10 overflow-hidden mb-14 overflow-x-auto" data-testid="bacheca-payments">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mute border-b border-white/10 bg-black/30">
                  <th className="text-left px-5 py-3">{t("dash_col_date")}</th>
                  <th className="text-left px-5 py-3">{t("dash_col_type")}</th>
                  <th className="text-left px-5 py-3">{t("dash_col_email")}</th>
                  <th className="text-left px-5 py-3">{t("dash_col_amount")}</th>
                  <th className="text-left px-5 py-3">{t("dash_col_status")}</th>
                  <th className="text-left px-5 py-3">{t("dash_col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {(paymentsData?.items || []).map((p) => (
                  <tr key={p.session_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-3 text-mist">{fmtDate(p.created_at)}</td>
                    <td className="px-5 py-3">{p.lookup_key === "unlock_full" ? t("type_unlock") : t("type_donation")}</td>
                    <td className="px-5 py-3 text-mist">{p.email || "—"}</td>
                    <td className="px-5 py-3 font-semibold">{fmtAmount(p.amount, p.currency)}</td>
                    <td className="px-5 py-3">
                      <span className={`font-mono2 text-[10px] tracking-widest uppercase px-2 py-1 rounded-full border ${statusColor(p.payment_status)}`}>
                        {p.payment_status === "paid" ? t("dash_paid") : p.payment_status === "failed" ? t("dash_failed") : p.payment_status === "refunded" ? t("dash_refunded") : t("dash_pending")}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {p.payment_status === "paid" && (
                          <>
                            <button onClick={() => refundPayment(p.session_id)} data-testid={`refund-${p.session_id}`} title={t("dash_refund")}
                              className="inline-flex items-center gap-1.5 font-mono2 text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full border border-white/15 text-mute hover:border-red-400 hover:text-red-400 transition-colors">
                              <RotateCcw className="w-3 h-3" /> {t("dash_refund")}
                            </button>
                            <button onClick={() => requestInvoice(p.session_id)} data-testid={`invoice-req-${p.session_id}`} title={t("b_inv_send_hint")}
                              className="inline-flex items-center gap-1.5 font-mono2 text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full border border-white/15 text-mute hover:border-ember hover:text-ember transition-colors">
                              <FileText className="w-3 h-3" /> {t("b_inv_send")}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {paymentsData && paymentsData.items.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-8 text-center text-mute">{t("dash_empty")}</td></tr>
                )}
              </tbody>
            </table>
            <div className="px-5 pb-4">
              <Pager data={paymentsData} page={pPage} setPage={setPPage} testid="payments-pager" />
            </div>
          </div>
        </Reveal>

        <Reveal id="bk-invoices" className="scroll-mt-32">
          <h2 className="font-display font-bold text-xl mb-5">{t("b_invoices")}</h2>
          <div className="grid lg:grid-cols-2 gap-6 mb-14">
            <div className="rounded-2xl border border-white/10 bg-card p-6" data-testid="fic-settings-card">
              <h3 className="font-display font-semibold mb-1">{t("b_inv_settings")}</h3>
              <p className="text-[11px] text-mute mb-5">
                {t("b_inv_where")}{" "}
                <span className={`font-mono2 text-[10px] uppercase tracking-widest ${fic?.configured ? "text-gold" : "text-red-400"}`}>
                  {fic ? (fic.configured ? t("b_inv_configured") : t("b_inv_not_configured")) : "…"}
                </span>
              </p>
              <form onSubmit={saveFic} className="space-y-3" data-testid="fic-settings-form">
                <input type="password" value={ficForm.access_token || ""} onChange={(e) => setFicForm({ ...ficForm, access_token: e.target.value })}
                  placeholder={fic?.token_set ? t("b_inv_token_set") : t("b_inv_token")} data-testid="fic-token-input"
                  className="w-full rounded-lg border border-white/15 bg-black/40 px-4 py-2.5 text-sm placeholder:text-mute focus:border-ember focus:outline-none" />
                <div className="grid grid-cols-2 gap-3">
                  <input value={ficForm.company_id ?? fic?.company_id ?? ""} onChange={(e) => setFicForm({ ...ficForm, company_id: e.target.value })}
                    placeholder={t("b_inv_company")} data-testid="fic-company-input"
                    className="rounded-lg border border-white/15 bg-black/40 px-4 py-2.5 text-sm placeholder:text-mute focus:border-ember focus:outline-none" />
                  <input value={ficForm.payment_method_id ?? fic?.payment_method_id ?? ""} onChange={(e) => setFicForm({ ...ficForm, payment_method_id: e.target.value })}
                    placeholder={t("b_inv_paymethod")} data-testid="fic-paymethod-input"
                    className="rounded-lg border border-white/15 bg-black/40 px-4 py-2.5 text-sm placeholder:text-mute focus:border-ember focus:outline-none" />
                  <input type="number" value={ficForm.vat_id ?? fic?.vat_id ?? 0} onChange={(e) => setFicForm({ ...ficForm, vat_id: e.target.value })}
                    placeholder={t("b_inv_vat")} title={t("b_inv_vat")} data-testid="fic-vat-input"
                    className="rounded-lg border border-white/15 bg-black/40 px-4 py-2.5 text-sm placeholder:text-mute focus:border-ember focus:outline-none" />
                  <div className="grid grid-cols-2 gap-3">
                    <input type="number" min={1} value={ficForm.next_invoice_number ?? fic?.next_invoice_number ?? 1}
                      onChange={(e) => setFicForm({ ...ficForm, next_invoice_number: e.target.value })} title={t("b_inv_next_f")} placeholder={t("b_inv_next_f")} data-testid="fic-nextf-input"
                      className="rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm placeholder:text-mute focus:border-ember focus:outline-none" />
                    <input type="number" min={1} value={ficForm.next_receipt_number ?? fic?.next_receipt_number ?? 1}
                      onChange={(e) => setFicForm({ ...ficForm, next_receipt_number: e.target.value })} title={t("b_inv_next_r")} placeholder={t("b_inv_next_r")} data-testid="fic-nextr-input"
                      className="rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm placeholder:text-mute focus:border-ember focus:outline-none" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="submit" data-testid="fic-save-btn" className="rounded-full bg-ember px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">{t("b_inv_save")}</button>
                  <button type="button" onClick={testFic} data-testid="fic-test-btn" className="rounded-full border border-white/15 px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-mist hover:border-ember hover:text-ember transition-colors">{t("b_inv_test")}</button>
                </div>
                {ficMsg && <p className={`text-xs ${ficMsg.ok ? "text-gold" : "text-red-400"}`} data-testid="fic-msg">{ficMsg.text}</p>}
              </form>
            </div>
            <div className="rounded-2xl border border-white/10 bg-card p-6" data-testid="invoice-requests-card">
              <h3 className="font-display font-semibold mb-5">{t("b_inv_requests")}</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                {(invoiceRequests || []).map((r) => (
                  <div key={r.request_id} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 pb-2" data-testid={`invoice-row-${r.request_id}`}>
                    <div>
                      <p className="text-sm text-mist">{r.email || "—"} <span className="text-mute">· {(r.amount / 100).toFixed(2)} € · {r.kind || "—"}</span></p>
                      <p className="text-[10px] font-mono2 text-mute">{fmtDate(r.created_at)}{r.number ? ` · n. ${r.number}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`font-mono2 text-[10px] tracking-widest uppercase px-2 py-1 rounded-full border ${
                        r.status === "issued" ? "text-gold border-gold/50 bg-gold/10"
                        : r.status === "data_submitted" ? "text-ember border-ember/50 bg-ember/10"
                        : r.status === "failed" ? "text-red-400 border-red-400/40 bg-red-400/10"
                        : "text-mist border-white/15"}`} data-testid={`invoice-status-${r.request_id}`}>
                        {t(`b_inv_st_${r.status}`)}
                      </span>
                      {r.status === "data_submitted" && (
                        <button onClick={() => issueDoc(r.request_id)} data-testid={`invoice-issue-${r.request_id}`}
                          className="rounded-full bg-ember px-4 py-1.5 text-[10px] font-mono2 uppercase tracking-widest text-black hover:bg-gold transition-colors">
                          {t("b_inv_issue")}
                        </button>
                      )}
                      {r.status === "failed" && (
                        <button onClick={() => issueDoc(r.request_id)} data-testid={`invoice-retry-${r.request_id}`} title={r.error || ""}
                          className="rounded-full border border-red-400/40 px-4 py-1.5 text-[10px] font-mono2 uppercase tracking-widest text-red-400 hover:bg-red-400/10 transition-colors">
                          {t("b_inv_retry")}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {invoiceRequests && invoiceRequests.length === 0 && <p className="text-mute text-sm">{t("dash_empty")}</p>}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal id="bk-newsletter" className="scroll-mt-32">
          <h2 className="font-display font-bold text-xl mb-5 flex items-center gap-2"><Megaphone className="w-5 h-5 text-gold" /> {t("b_newsletter")}</h2>
          <div className="grid lg:grid-cols-2 gap-6 mb-14">
            <form
              data-testid="newsletter-send-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const subject = e.target.subject.value;
                const message = e.target.message.value;
                setSendState("busy");
                try {
                  const { data } = await api.post("/admin/newsletter/send", { subject, message });
                  setSendState(`ok:${data.sent}`);
                  e.target.reset();
                } catch {
                  setSendState("error");
                }
              }}
              className="rounded-xl border border-white/10 bg-card p-6 space-y-4"
            >
              <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute">{t("b_send_title")}</p>
              <input name="subject" required minLength={3} data-testid="newsletter-subject-input" placeholder={t("b_subject")}
                className="w-full rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-sm placeholder:text-mute focus:border-ember focus:outline-none" />
              <textarea name="message" required minLength={10} rows={4} data-testid="newsletter-message-input" placeholder={t("b_message")}
                className="w-full rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-sm placeholder:text-mute focus:border-ember focus:outline-none resize-none" />
              <div className="flex items-center gap-4">
                <button data-testid="newsletter-send-button" disabled={sendState === "busy"}
                  className="inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors disabled:opacity-50">
                  {t("b_send")} <Send className="w-3.5 h-3.5" />
                </button>
                {sendState?.startsWith("ok:") && <span className="text-gold text-sm" data-testid="newsletter-sent-note">{t("b_sent")} ({sendState.split(":")[1]})</span>}
                {sendState === "error" && <span className="text-red-400 text-sm">Errore</span>}
              </div>
            </form>
            <div className="rounded-xl border border-white/10 bg-card p-6">
              <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute mb-4">{t("b_subs_list")}</p>
              <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="newsletter-subs-list">
                {(data?.newsletter || []).map((s) => (
                  <div key={s.sub_id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                    <span className="text-mist">{s.email}</span>
                    <span className={`font-mono2 text-[10px] tracking-widest uppercase ${s.active ? "text-gold" : "text-mute"}`}>
                      {s.active ? t("b_active") : t("b_unsub")}
                    </span>
                  </div>
                ))}
                {data && data.newsletter.length === 0 && <p className="text-mute text-sm">{t("dash_empty")}</p>}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal id="bk-promos" className="scroll-mt-32">
          <h2 className="font-display font-bold text-xl mb-5 flex items-center gap-2"><Ticket className="w-5 h-5 text-ember" /> {t("b_promos")}</h2>
          <div className="rounded-xl border border-white/10 bg-card p-6 mb-14" data-testid="promo-admin-section">
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const code = e.target.code.value.trim();
                const days = Number(e.target.days.value) || 7;
                const discount = promoKind === "discount" ? Math.min(90, Math.max(1, Number(e.target.discount?.value) || 0)) : 0;
                try {
                  const { data: created } = await api.post("/admin/promos", { code: code || null, days, discount_percent: discount });
                  setData((d) => ({ ...d, promos: [created, ...d.promos] }));
                  e.target.reset();
                } catch { /* esiste già */ }
              }}
              className="flex flex-col sm:flex-row gap-3 mb-5"
              data-testid="promo-create-form"
            >
              <select value={promoKind} onChange={(e) => setPromoKind(e.target.value)} data-testid="promo-kind-select"
                className="w-full sm:w-64 rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-sm focus:border-ember focus:outline-none">
                <option value="free">{t("b_promo_free")}</option>
                <option value="discount">{t("b_promo_kind_discount")}</option>
              </select>
              <input name="code" data-testid="promo-new-code-input" placeholder={t("b_promo_optional")}
                className="flex-1 rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-sm placeholder:text-mute focus:border-ember focus:outline-none" />
              <input name="days" type="number" min={1} max={365} defaultValue={7} data-testid="promo-days-input"
                className="w-full sm:w-40 rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-sm focus:border-ember focus:outline-none" title={t("b_promo_days")} />
              {promoKind === "discount" && (
                <input name="discount" type="number" min={1} max={90} defaultValue={10} required data-testid="promo-discount-input"
                  className="w-full sm:w-56 rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-sm placeholder:text-mute focus:border-ember focus:outline-none" title={t("b_promo_discount")} placeholder={t("b_promo_discount")} />
              )}
              <button data-testid="promo-create-button"
                className="rounded-full bg-ember px-6 py-3 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors whitespace-nowrap">
                {t("b_promo_create")}
              </button>
            </form>
            <div className="space-y-2">
              {(data?.promos || []).map((p) => (
                <div key={p.code} className={`flex flex-wrap items-center justify-between gap-2 border-b border-white/5 pb-2 ${p.suspended ? "opacity-50" : ""}`} data-testid={`promo-row-${p.code}`}>
                  <span onClick={() => navigator.clipboard?.writeText(p.code)} title={t("b_promo_copy_hint")}
                    className={`font-mono2 font-bold tracking-widest cursor-pointer hover:text-white transition-colors ${p.suspended ? "text-mute line-through" : "text-gold"}`}>{p.code}</span>
                  <span className="text-xs text-mute">
                    {t("b_promo_expires")} {fmtDate(p.expires_at)} · {p.uses} {t("b_promo_uses")}
                    {p.discount_percent > 0 && <span className="ml-2 text-ember">(−{p.discount_percent}%)</span>}
                    {!p.discount_percent && !p.auto && <span className="ml-2 text-gold">({t("b_promo_free_badge")})</span>}
                    {p.auto && <span className="ml-2 text-viola">({t("b_promo_auto")})</span>}
                    {p.expired && <span className="ml-2 text-red-400">(scaduto)</span>}
                    {p.suspended && <span className="ml-2 text-red-400" data-testid={`promo-suspended-badge-${p.code}`}>({t("b_promo_suspended")})</span>}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`promo-suspend-${p.code}`}
                      title={p.suspended ? t("b_promo_resume") : t("b_promo_suspend")}
                      onClick={async () => {
                        const { data: res } = await api.patch(`/admin/promos/${p.code}/suspend`);
                        setData((d) => ({ ...d, promos: d.promos.map((x) => (x.code === p.code ? { ...x, suspended: res.suspended } : x)) }));
                      }}
                      className={`transition-colors ${p.suspended ? "text-gold hover:text-ember" : "text-mute hover:text-gold"}`}
                    >
                      <Ban className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`promo-delete-${p.code}`}
                      onClick={async () => {
                        await api.delete(`/admin/promos/${p.code}`);
                        setData((d) => ({ ...d, promos: d.promos.filter((x) => x.code !== p.code) }));
                      }}
                      className="text-mute hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <p className="font-mono2 text-[10px] tracking-wider text-mute mt-5 uppercase">{t("b_promo_note")}</p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-10">
          <Reveal id="bk-readers" className="scroll-mt-32">
            <h2 className="font-display font-bold text-xl mb-5">{t("dash_readers_list")}</h2>
            <input
              data-testid="readers-search-input"
              value={rSearch}
              onChange={(e) => { setRSearch(e.target.value); setRPage(1); }}
              placeholder="Cerca lettore…"
              className="w-full rounded-lg border border-white/15 bg-black/40 px-4 py-2.5 text-sm placeholder:text-mute focus:border-ember focus:outline-none mb-4"
            />
            <div className="space-y-3" data-testid="bacheca-readers">
              {(readersData?.items || []).map((u) => (
                <div key={u.user_id} className="rounded-xl border border-white/10 bg-card px-5 py-4" data-testid={`reader-row-${u.user_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold text-sm">{u.name || "—"}</p>
                      <p className="text-xs text-mute">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono2 text-mute hidden sm:inline">{fmtDate(u.created_at)}</span>
                      {u.unlocked && (
                        <span className="font-mono2 text-[10px] tracking-widest uppercase px-2 py-1 rounded-full border text-gold border-gold/50 bg-gold/10">
                          {t("dash_unlocked_badge")}
                        </span>
                      )}
                      {u.suspended && (
                        <span className="font-mono2 text-[10px] tracking-widest uppercase px-2 py-1 rounded-full border text-red-400 border-red-400/50 bg-red-400/10">
                          {t("b_suspended")}
                        </span>
                      )}
                      {u.role !== "admin" && (
                        <>
                          <button onClick={() => toggleReading(u)} data-testid={`reader-reading-${u.user_id}`} title={t("b_reading")}
                            className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-mist hover:text-ember hover:border-ember transition-colors">
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => toggleSuspend(u)} data-testid={`reader-suspend-${u.user_id}`} title={u.suspended ? t("b_unsuspend") : t("b_suspend")}
                            className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${u.suspended ? "border-gold/50 text-gold hover:bg-gold hover:text-black" : "border-white/15 text-mist hover:text-red-400 hover:border-red-400"}`}>
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {openReading === u.user_id && (
                    <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5" data-testid={`reader-reading-list-${u.user_id}`}>
                      <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute mb-2">{t("b_reading")}</p>
                      {(readingMap[u.user_id] || []).map((r) => (
                        <div key={r.chapter_id} className="flex items-center gap-3 text-xs">
                          <span className="font-mono2 text-ember w-14">CAP. {r.chapter_id}</span>
                          <div className="flex-1 h-1.5 rounded-full bg-white/10">
                            <div className="h-full rounded-full bg-gradient-to-r from-ember to-gold" style={{ width: `${r.percent}%` }} />
                          </div>
                          <span className="font-mono2 text-mute w-10 text-right">{r.percent}%</span>
                        </div>
                      ))}
                      {(readingMap[u.user_id] || []).length === 0 && <p className="text-mute text-xs">{t("dash_empty")}</p>}
                    </div>
                  )}
                </div>
              ))}
              {readersData && readersData.items.length === 0 && <p className="text-mute text-sm">{t("dash_empty")}</p>}
            </div>
            <Pager data={readersData} page={rPage} setPage={setRPage} testid="readers-pager" />
          </Reveal>

          <Reveal delay={0.1} id="bk-contacts" className="scroll-mt-32">
            <h2 className="font-display font-bold text-xl mb-5">{t("dash_contacts_list")}</h2>
            <div className="space-y-3" data-testid="bacheca-contacts">
              {(data?.contacts || []).map((c) => (
                <div key={c.contact_id} className={`rounded-xl border px-5 py-4 transition-colors ${c.read ? "border-white/5 bg-card/50 opacity-70" : "border-white/10 bg-card"}`} data-testid={`contact-item-${c.contact_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-sm">{c.name} <span className="text-mute font-normal">· {c.email}</span></p>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleContactRead(c.contact_id)} data-testid={`contact-read-toggle-${c.contact_id}`}
                        title={c.read ? t("dash_mark_unread") : t("dash_mark_read")}
                        className={`rounded-full p-1.5 border transition-colors ${c.read ? "text-ember border-ember/50 bg-ember/10" : "text-mute border-white/10 hover:text-ember hover:border-ember/50"}`}>
                        <Smile className="w-4 h-4" />
                      </button>
                      <span className="font-mono2 text-[10px] tracking-widest uppercase px-2 py-1 rounded-full border text-ember border-ember/50 bg-ember/10">
                        {t(`topic_${c.topic}`)}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-mist leading-relaxed line-clamp-3">{c.message}</p>
                  <p className="text-[10px] font-mono2 text-mute mt-2">{fmtDate(c.created_at)}</p>
                </div>
              ))}
              {data && data.contacts.length === 0 && <p className="text-mute text-sm">{t("dash_empty")}</p>}
            </div>
          </Reveal>
        </div>

        <Reveal id="bk-comments" className="scroll-mt-32">
          <h2 className="font-display font-bold text-xl mb-5 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-viola" /> {t("b_comments")}</h2>
          <div className="rounded-xl border border-white/10 bg-card p-6 mb-14" data-testid="bacheca-comments">
            {(data?.comments || []).length === 0 && <p className="text-mute text-sm">{t("dash_empty")}</p>}
            <div className="space-y-3">
              {(data?.comments || []).map((c) => (
                <div key={c.comment_id} className="border-b border-white/5 pb-3" data-testid={`admin-comment-${c.comment_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                    <p className="text-xs text-mute"><span className="text-white">{c.user_name}</span> · CAP. {c.chapter_id} · {fmtDate(c.created_at)}</p>
                    <div className="flex items-center gap-2">
                      <span className={`font-mono2 text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${c.status === "published" ? "text-gold border-gold/50" : "text-mute border-white/15"}`}>
                        {c.status === "published" ? t("b_published") : t("b_pending")}
                      </span>
                      <button
                        data-testid={`comment-publish-${c.comment_id}`}
                        onClick={async () => {
                          await api.post(`/admin/comments/${c.comment_id}/publish`, { publish: c.status !== "published" });
                          setData((d) => ({ ...d, comments: d.comments.map((x) => x.comment_id === c.comment_id ? { ...x, status: c.status === "published" ? "pending" : "published" } : x) }));
                        }}
                        className="rounded-full border border-ember/50 px-3 py-1 text-[10px] font-mono2 tracking-widest uppercase text-ember hover:bg-ember hover:text-black transition-colors"
                      >
                        {c.status === "published" ? t("b_unpublish") : t("b_publish")}
                      </button>
                      <button
                        data-testid={`comment-delete-${c.comment_id}`}
                        onClick={async () => {
                          await api.delete(`/admin/comments/${c.comment_id}`);
                          setData((d) => ({ ...d, comments: d.comments.filter((x) => x.comment_id !== c.comment_id) }));
                        }}
                        className="text-mute hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-mist leading-relaxed">{c.text}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal id="bk-config" className="scroll-mt-32">
          <h2 className="font-display font-bold text-xl mb-2 flex items-center gap-2"><Building2 className="w-5 h-5 text-ember" /> {t("b_co_title")}</h2>
          <p className="text-mute text-xs mb-5">{t("b_co_sub")}</p>
          <div className="rounded-xl border border-white/10 bg-card p-6 mb-14" data-testid="company-card">
            {coForm && (
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  ["name", t("b_co_name")], ["country", t("b_co_country")], ["vat", t("b_co_vat")],
                  ["sdi", t("b_co_sdi")], ["address", t("b_co_addr")], ["web", t("b_co_web")], ["email", t("b_co_email")],
                ].map(([k, label]) => (
                  <div key={k}>
                    <label className="block text-xs text-mute mb-1.5">{label}</label>
                    <input value={coForm[k] || ""} onChange={(e) => setCoForm({ ...coForm, [k]: e.target.value })}
                      data-testid={`company-input-${k}`}
                      className="w-full rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-sm focus:border-ember focus:outline-none" />
                  </div>
                ))}
                <div className="sm:col-span-2 flex items-center gap-4">
                  <button onClick={saveCompany} data-testid="company-save"
                    className="rounded-full bg-ember px-6 py-3 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">
                    {t("b_notify_save")}
                  </button>
                  {coMsg && <p className={`text-sm ${coMsg.ok ? "text-gold" : "text-red-400"}`} data-testid="company-msg">{coMsg.text}</p>}
                </div>
              </div>
            )}
          </div>
        </Reveal>

        <Reveal>
          <h2 className="font-display font-bold text-xl mb-2 flex items-center gap-2"><Mail className="w-5 h-5 text-viola" /> {t("b_notify_title")}</h2>
          <p className="text-mute text-xs mb-5">{t("b_notify_sub")}</p>
          <div className="rounded-xl border border-white/10 bg-card p-6 mb-14 flex flex-wrap items-center gap-3" data-testid="notify-email-card">
            <input type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)}
              data-testid="notify-email-input"
              className="flex-1 min-w-[220px] rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-sm placeholder:text-mute focus:border-ember focus:outline-none" />
            <button onClick={saveNotifyEmail} data-testid="notify-email-save"
              className="rounded-full bg-ember px-6 py-3 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">
              {t("b_notify_save")}
            </button>
            {notifyMsg && <p className={`text-sm w-full ${notifyMsg.ok ? "text-gold" : "text-red-400"}`} data-testid="notify-email-msg">{notifyMsg.text}</p>}
          </div>
        </Reveal>

        <Reveal id="bk-stats" className="scroll-mt-32">
          <h2 className="font-display font-bold text-xl mb-2 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-ember" /> {t("rs_title")}</h2>          <p className="text-mute text-xs mb-5">{t("rs_sub")}</p>
          <div className="rounded-xl border border-white/10 bg-card p-6 mb-14" data-testid="reader-stats">
            <p className="text-sm mb-6" data-testid="reader-stats-totals">
              <span className="font-display font-extrabold text-2xl">{data?.rstats?.total_readers ?? "—"}</span> <span className="text-mute">{t("rs_readers")}</span>
              <span className="mx-3 text-white/20">·</span>
              <span className="font-display font-extrabold text-2xl text-gold">{data?.rstats?.unlocked ?? "—"}</span> <span className="text-mute">{t("rs_unlocked")}</span>
            </p>
            {!(data?.rstats?.chapters || []).some((c) => c.started > 0) && (
              <p className="text-sm text-mute" data-testid="reader-stats-empty">{t("rs_no_data")}</p>
            )}
            <div className="space-y-2.5">
              {(data?.rstats?.chapters || []).filter((c) => c.started > 0).map((c) => {
                const scale = Math.max(...(data?.rstats?.chapters || []).map((x) => x.started), 1);
                return (
                  <div key={c.chapter_id} className="flex items-center gap-3" data-testid={`reader-stats-ch-${c.chapter_id}`}>
                    <span className="font-mono2 text-xs text-gold w-8 shrink-0">{c.num}</span>
                    <div className="flex-1 h-4 rounded-full bg-white/5 overflow-hidden relative">
                      <div className="absolute inset-y-0 left-0 rounded-full bg-ember/25" style={{ width: `${(c.started / scale) * 100}%` }} />
                      <div className="absolute inset-y-0 left-0 rounded-full bg-ember" style={{ width: `${(c.finished / scale) * 100}%` }} />
                    </div>
                    <span className="text-[11px] text-mute w-52 sm:w-64 shrink-0 text-right inline-flex items-center justify-end gap-2">
                      {c.started} {t("rs_started")} · {c.finished} {t("rs_finished")} · {c.avg_percent}% {t("rs_avg")}
                      {c.bookmarks > 0 && <span className="inline-flex items-center gap-0.5 text-gold"><Bookmark className="w-3 h-3" />{c.bookmarks}</span>}
                      {c.comments > 0 && <span className="inline-flex items-center gap-0.5 text-viola"><MessageSquare className="w-3 h-3" />{c.comments}</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal id="bk-guardian" className="scroll-mt-32">
          <h2 className="font-display font-bold text-xl mb-2 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-gold" /> {t("g_title")}</h2>
          <p className="text-mute text-xs mb-5">{t("g_sub")}</p>
          <div className="rounded-xl border border-white/10 bg-card p-6 mb-14" data-testid="guardian-section">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {[
                { k: "errors_24h", label: t("g_errors"), v: data?.guardian?.errors_24h, testid: "g-errors" },
                { k: "events_24h", label: t("g_events"), v: data?.guardian?.events_24h, testid: "g-events" },
                { k: "mail_queue", label: t("g_mailq"), v: data?.guardian?.mail_queue, testid: "g-mailq" },
                { k: "paid", label: t("g_paid"), v: data?.guardian?.funnel?.payment_ok, testid: "g-paid" },
              ].map((s) => (
                <div key={s.k} className="rounded-lg border border-white/10 bg-black/30 p-4 text-center" data-testid={s.testid}>
                  <p className="font-display font-extrabold text-2xl">{s.v ?? "—"}</p>
                  <p className="font-mono2 text-[10px] tracking-widest uppercase text-mute mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mute mb-3">{t("g_funnel")}</p>
            <div className="flex flex-wrap items-center gap-2 mb-8 text-xs" data-testid="guardian-funnel">
              <span className="rounded-full border border-white/10 px-3 py-1 text-mist">{data?.guardian?.funnel?.page_view ?? 0} {t("g_views")}</span>
              <span className="text-mute">→</span>
              <span className="rounded-full border border-white/10 px-3 py-1 text-mist">{data?.guardian?.funnel?.unlock_click ?? 0} {t("g_unlocks")}</span>
              <span className="text-mute">→</span>
              <span className="rounded-full border border-white/10 px-3 py-1 text-mist">{data?.guardian?.funnel?.donate_click ?? 0} {t("g_donations")}</span>
              <span className="text-mute">→</span>
              <span className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-gold">{data?.guardian?.funnel?.payment_ok ?? 0} {t("g_paid")}</span>
              <span className="rounded-full border border-red-400/30 px-3 py-1 text-red-300/80">{data?.guardian?.funnel?.payment_cancel ?? 0} {t("g_cancelled")}</span>
            </div>

            <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mute mb-3">{t("g_top_errors")}</p>
            {(data?.guardian?.top_errors || []).length === 0 && <p className="text-sm text-gold mb-8" data-testid="guardian-no-errors">{t("g_no_errors")}</p>}
            <div className="space-y-2 mb-8">
              {(data?.guardian?.top_errors || []).map((e, i) => (
                <p key={i} className="text-xs text-mute" data-testid={`guardian-top-error-${i}`}>
                  <span className="text-red-400 font-mono2">{e.n}×</span> {e.message} <span className="text-mute/60">({e.url})</span>
                </p>
              ))}
            </div>

            {data?.guardian?.last_report?.diagnosis && (
              <div className="rounded-lg border border-viola/40 bg-viola/5 p-5 mb-8" data-testid="guardian-diagnosis">
                <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-viola mb-3">
                  {t("g_diagnosis")} · {fmtDate(data.guardian.last_report.created_at)}
                </p>
                <p className="text-sm text-mist leading-relaxed whitespace-pre-wrap">{data.guardian.last_report.diagnosis}</p>
              </div>
            )}

            <button onClick={runGuardian} disabled={gBusy} data-testid="guardian-run-button"
              className="rounded-full border border-gold/50 px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-gold hover:bg-gold hover:text-black transition-colors disabled:opacity-40 mb-10">
              {gBusy ? t("g_running") : t("g_run")}
            </button>

            <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mute mb-3">{t("g_actions")}</p>
            {(data?.gactions || []).length === 0 && <p className="text-sm text-mute mb-8">{t("g_no_actions")}</p>}
            <div className="space-y-2 mb-10">
              {(data?.gactions || []).map((a) => (
                <div key={a.action_id} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 pb-2" data-testid={`guardian-action-${a.action_id}`}>
                  <p className="text-xs text-mist flex-1 min-w-[200px]">
                    <span className="text-mute mr-2">{fmtDate(a.created_at)}</span>{a.description}
                  </p>
                  <div className="flex items-center gap-2">
                    {a.status === "rolled_back" ? (
                      <span className="font-mono2 text-[10px] tracking-widest uppercase text-mute">({t("g_rolled")})</span>
                    ) : a.reversible ? (
                      <button onClick={() => rollbackAction(a.action_id)} data-testid={`guardian-rollback-${a.action_id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-ember/50 px-3 py-1 text-[10px] font-mono2 tracking-widest uppercase text-ember hover:bg-ember hover:text-black transition-colors">
                        <RotateCcw className="w-3 h-3" /> {t("g_rollback")}
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mute mb-3">{t("g_versions")}</p>
            <div className="space-y-2" data-testid="guardian-versions">
              {(data?.gversions || []).map((v) => (
                <div key={v.hash} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 pb-2" data-testid={`guardian-version-${v.hash}`}>
                  <p className="text-xs text-mute flex-1 min-w-[200px]"><span className="font-mono2 text-gold mr-2">{v.hash}</span>{v.message}</p>
                  <button onClick={() => requestVersionRollback(v.hash)} data-testid={`guardian-restore-${v.hash}`}
                    className="rounded-full border border-white/15 px-3 py-1 text-[10px] font-mono2 tracking-widest uppercase text-mist hover:border-ember hover:text-ember transition-colors">
                    {restoreMsg === v.hash ? t("g_restore_sent") : t("g_restore")}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal id="bk-errors" className="scroll-mt-32">
          <h2 className="font-display font-bold text-xl mt-14 mb-5 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-400" /> {t("b_errors")}</h2>
          <div className="rounded-xl border border-white/10 bg-card p-6" data-testid="bacheca-errors">
            {(errorsData?.items || []).length === 0 ? (
              <p className="text-mute text-sm">{t("b_no_errors")}</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {errorsData.items.map((e) => (
                  <div key={e.err_id} className="text-xs border-b border-white/5 pb-2">
                    <div className="flex items-center gap-3 mb-1">
                      <span className={`font-mono2 text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${e.source === "server" ? "text-red-400 border-red-400/40" : "text-viola border-viola/40"}`}>{e.source}</span>
                      <span className="font-mono2 text-mute">{fmtDate(e.created_at)}</span>
                      <span className="font-mono2 text-mute truncate">{e.url}</span>
                    </div>
                    <p className="text-mist">{e.message}</p>
                  </div>
                ))}
              </div>
            )}
            <Pager data={errorsData} page={ePage} setPage={setEPage} testid="errors-pager" />
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default Bacheca;
